/* eslint-disable @typescript-eslint/no-explicit-any */
import { context, trace, defaultTextMapSetter } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import type { CustomSpan } from '@packages/shared/core/common/observability/entities';
import { ObservabilitySystem } from '@packages/shared/core/common/observability/interfaces';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { HttpClient, HttpMethod, HttpRequestConfig } from '../core/HttpClient';
import { HttpClientDecorator } from '../core/HttpClientDecorator';
import {
  UUID_AND_NUMERIC_REGEXP,
  UUID_REGEXP,
  EMAIL_REGEXP,
  LONG_STRING_REGEXP,
} from '../constants/spanNameNormalization';

export class WithObservability extends HttpClientDecorator {
  constructor(
    httpClient: HttpClient,
    private readonly observabilitySystem: ObservabilitySystem,
  ) {
    super(httpClient);
  }

  async get<ResponseData = any, ResponseMeta = any>(url: string, config?: HttpRequestConfig) {
    return this.observabilitySystem.startActiveSpan({
      name: this.normalizeSpanName(HttpMethod.GET, url),
      fn: async (activeSpan) => {
        try {
          const mergedConfig = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.get<ResponseData, ResponseMeta>(url, mergedConfig);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          activeSpan.fail(this.normalizeErrorForSpan(err));

          throw err;
        }
      },
    });
  }

  async post<ResponseData = any, ResponseMeta = any>(url: string, config?: HttpRequestConfig) {
    return this.observabilitySystem.startActiveSpan({
      name: this.normalizeSpanName(HttpMethod.POST, url),
      fn: async (activeSpan) => {
        try {
          const mergedConfig = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.post<ResponseData, ResponseMeta>(url, mergedConfig);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          activeSpan.fail(this.normalizeErrorForSpan(err));

          throw err;
        }
      },
    });
  }

  async put<ResponseData = any, ResponseMeta = any>(url: string, config?: HttpRequestConfig) {
    return this.observabilitySystem.startActiveSpan({
      name: this.normalizeSpanName(HttpMethod.PUT, url),
      fn: async (activeSpan) => {
        try {
          const mergedConfig = this.mergeConfigWithTrace(config, activeSpan);
          const result = await super.put<ResponseData, ResponseMeta>(url, mergedConfig);

          activeSpan.succeed();

          return result;
        } catch (err: unknown) {
          activeSpan.fail(this.normalizeErrorForSpan(err));

          throw err;
        }
      },
    });
  }

  private normalizeSpanName(method: HttpMethod, url: string): string {
    const normalizedUrl = url
      .replace(UUID_AND_NUMERIC_REGEXP, '_uuid_or_int_')
      .replace(EMAIL_REGEXP, '_email_')
      .replace(LONG_STRING_REGEXP, '_long_string_');

    return `${method} ${normalizedUrl}`;
  }

  private normalizeErrorForSpan(err: unknown): Error {
    const error = normalizeError(err);
    const normalizedMessage = error.message.replace(UUID_REGEXP, '_uuid_or_int_').replace(EMAIL_REGEXP, '_email_');
    const spanError = new Error(normalizedMessage);

    spanError.name = error.name;
    spanError.stack = error.stack;

    return spanError;
  }

  private getTraceHeaders(activeSpan?: CustomSpan): Record<string, string> {
    if (!activeSpan) {
      return {};
    }

    const ctxForInject = trace.setSpan(context.active(), trace.wrapSpanContext(activeSpan.spanContext()));
    const headers: Record<string, string> = {};

    new W3CTraceContextPropagator().inject(ctxForInject, headers, defaultTextMapSetter);

    return headers;
  }

  private mergeConfigWithTrace(config: HttpRequestConfig = {}, span?: CustomSpan): HttpRequestConfig {
    const traceHeaders = this.getTraceHeaders(span);

    return {
      ...config,
      headers: {
        ...config.headers,
        ...traceHeaders,
      },
    };
  }
}
